import React, { FC, useCallback, useEffect, useState } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { useNavigate } from 'react-router-dom';
import { Unity, useUnityContext } from 'react-unity-webgl';

import Lottie from 'lottie-react';
import { ReactUnityEventParameter } from 'react-unity-webgl/distribution/types/react-unity-event-parameters';

import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {
  Box,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';

import catAnim from '@assets/lottie/catAnim.json';

import { useResponsiveUnity } from './hooks';
import { UnityComponentProps } from './types';

import './index.scss';

// Добавляем Google Font
// const fontStyle = {
//   fontFamily: "'Comfortaa', sans-serif",
//   fontWeight: 700,
//   fontSize: '32px',
// };

const getLoadingText = (percentage: number): string => {
  if (percentage < 25) return 'Собираем игру для вас с любовью';
  if (percentage < 50) return 'Загружаем игровые ресурсы';
  if (percentage < 90) return 'Почти готово!';
  return 'Мы не зависли, просто финальные штрихи';
};

export const UnityComponent: FC<UnityComponentProps> = ({
  loaderUrl,
  dataUrl,
  frameworkUrl,
  isFullScreenEnabled = false,
  codeUrl,
  resolution,
}) => {
  const [devicePixelRatio, setDevicePixelRatio] = useState(
    window.devicePixelRatio
  );
  const [unityError, setUnityError] = useState<string | null>(null);
  const [loadingTimeout, setLoadingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [lastProgressValue, setLastProgressValue] = useState<number>(0);
  const [fileLoadErrors, setFileLoadErrors] = useState<Record<string, string>>({});
  const [stuckAt90StartTime, setStuckAt90StartTime] = useState<number | null>(null);
  const [autoReloadTriggered, setAutoReloadTriggered] = useState<boolean>(false);
  const [activeDownloads, setActiveDownloads] = useState<Set<string>>(new Set());

  // Отслеживание активных загрузок через PerformanceObserver
  useEffect(() => {
    if (!('PerformanceObserver' in window)) {
      return;
    }

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries() as PerformanceResourceTiming[];
        
        entries.forEach(entry => {
          const url = entry.name.toLowerCase();
          const isUnityFile = url.includes('loader.js') || 
                             url.includes('data.br') || 
                             url.includes('framework.js.br') || 
                             url.includes('wasm.br');
          
          if (isUnityFile) {
            const fileName = entry.name.split('/').pop() || 'unknown';
            
            // Если duration = 0, значит запрос еще не завершен (активная загрузка)
            if (entry.duration === 0) {
              setActiveDownloads(prev => new Set(prev).add(fileName));
              console.log(`📥 Начата загрузка: ${fileName}`);
            } else {
              // Загрузка завершена
              setActiveDownloads(prev => {
                const newSet = new Set(prev);
                newSet.delete(fileName);
                return newSet;
              });
              console.log(`✅ Загрузка завершена: ${fileName} (${(entry.duration / 1000).toFixed(2)}с)`);
            }
          }
        });
      });

      observer.observe({ entryTypes: ['resource'] });

      return () => {
        observer.disconnect();
      };
    } catch (error) {
      console.error('Ошибка при создании PerformanceObserver:', error);
    }
  }, []);

  // Функция для проверки активных загрузок Unity файлов
  const checkActiveDownloads = useCallback((): boolean => {
    try {
      // Проверяем через состояние activeDownloads
      if (activeDownloads.size > 0) {
        console.log(`📥 Обнаружено ${activeDownloads.size} активных загрузок Unity файлов:`);
        activeDownloads.forEach(file => {
          console.log(`  - ${file}: загружается...`);
        });
        return true;
      }

      // Дополнительная проверка через Performance API - последние запросы за последние 3 секунды
      if ('performance' in window && 'getEntriesByType' in performance) {
        const networkEntries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        const now = performance.now();
        const threeSecondsAgo = now - 3000; // Последние 3 секунды
        
        const unityFiles = networkEntries.filter(entry => {
          const url = entry.name.toLowerCase();
          const isUnityFile = url.includes('loader.js') || 
                             url.includes('data.br') || 
                             url.includes('framework.js.br') || 
                             url.includes('wasm.br');
          
          // Проверяем, был ли запрос недавно (за последние 3 секунды)
          const startTime = entry.startTime || 0;
          const duration = entry.duration || 0;
          const endTime = startTime + duration;
          const isRecent = endTime > threeSecondsAgo || (startTime > threeSecondsAgo && duration === 0);
          
          return isUnityFile && isRecent;
        });

        if (unityFiles.length > 0) {
          // Проверяем, есть ли запросы, которые еще не завершены (duration = 0)
          const incompleteRequests = unityFiles.filter(file => file.duration === 0);

          if (incompleteRequests.length > 0) {
            console.log(`📥 Обнаружено ${incompleteRequests.length} незавершенных загрузок Unity файлов`);
            return true;
          }

          // Если все запросы завершены, но были недавно - возможно еще идет обработка
          const recentCompleted = unityFiles.filter(file => {
            const endTime = (file.startTime || 0) + (file.duration || 0);
            return endTime > threeSecondsAgo;
          });

          if (recentCompleted.length > 0) {
            console.log(`📥 Обнаружено ${recentCompleted.length} недавно завершенных загрузок Unity файлов`);
            console.log('⏳ Возможно, идет обработка загруженных файлов...');
            return true; // Считаем, что еще идет обработка
          }
        }
      }
      
      console.log('❌ Активных загрузок Unity файлов не обнаружено');
      return false;
    } catch (error) {
      console.error('Ошибка при проверке активных загрузок:', error);
      return false; // В случае ошибки считаем, что загрузок нет
    }
  }, [activeDownloads]);

  // Функция для автоматической очистки кэша и перезагрузки
  const autoClearCacheAndReload = useCallback(async () => {
    if (autoReloadTriggered) {
      return; // Уже запущена перезагрузка
    }

    setAutoReloadTriggered(true);
    console.log('🔄 Автоматическая очистка кэша и перезагрузка...');

    try {
      // Очищаем кэш через Cache API
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('✅ Кэш очищен через Cache API');
      }

      // Перезагружаем страницу с параметром для обхода кэша
      const currentUrl = window.location.href.split('?')[0];
      const nocacheParam = `nocache=${Date.now()}`;
      const newUrl = currentUrl.includes('?') 
        ? `${currentUrl}&${nocacheParam}`
        : `${currentUrl}?${nocacheParam}`;
      
      console.log('🔄 Перезагрузка страницы с обходом кэша...');
      window.location.href = newUrl;
    } catch (error) {
      console.error('❌ Ошибка при автоматической очистке кэша:', error);
      // Если не удалось очистить кэш, просто перезагружаем
      window.location.reload();
    }
  }, [autoReloadTriggered]);

  const unityContext = useUnityContext({
    loaderUrl,
    dataUrl,
    frameworkUrl,
    codeUrl,
  });

  useResponsiveUnity(resolution);

  const {
    unityProvider,
    loadingProgression,
    isLoaded,
    addEventListener,
    removeEventListener,
  } = unityContext;

  const handle = useFullScreenHandle();

  const navigate = useNavigate();

  const reloadPage = () => {
    navigate('/game');
  };

  useEffect(() => {
    addEventListener('ExitGame', reloadPage as () => ReactUnityEventParameter);

    return () => {
      removeEventListener(
        'ExitGame',
        reloadPage as () => ReactUnityEventParameter
      );
    };
  }, [addEventListener, removeEventListener]);

  useEffect(() => {
    const updateDevicePixelRatio = function () {
      setDevicePixelRatio(window.devicePixelRatio);
    };
    const mediaMatcher = window.matchMedia(
      `screen and (resolution: ${devicePixelRatio}dppx)`
    );

    mediaMatcher.addEventListener('change', updateDevicePixelRatio);

    return () => {
      mediaMatcher.removeEventListener('change', updateDevicePixelRatio);
    };
  }, [devicePixelRatio]);

  // Проверка доступности Unity файлов перед загрузкой
  useEffect(() => {
    const checkFilesAvailability = async () => {
      console.log('🔍 Проверка доступности Unity файлов...');
      const filesToCheck = [
        { name: 'loader.js', url: loaderUrl },
        { name: 'data.br', url: dataUrl },
        { name: 'framework.js.br', url: frameworkUrl },
        { name: 'wasm.br', url: codeUrl },
      ];

      const errors: Record<string, string> = {};

      for (const file of filesToCheck) {
        try {
          console.log(`📡 Проверка файла: ${file.name} (${file.url})`);
          const response = await fetch(file.url, {
            method: 'HEAD',
            cache: 'no-cache',
          });

          if (!response.ok) {
            const errorMsg = `HTTP ${response.status}: ${response.statusText}`;
            console.error(`❌ Файл ${file.name} недоступен: ${errorMsg}`);
            errors[file.name] = errorMsg;
          } else {
            console.log(`✅ Файл ${file.name} доступен (${response.headers.get('Content-Length')} bytes)`);
          }
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : 'Неизвестная ошибка';
          console.error(`❌ Ошибка при проверке файла ${file.name}:`, errorMsg);
          errors[file.name] = errorMsg;
        }
      }

      if (Object.keys(errors).length > 0) {
        setFileLoadErrors(errors);
        const errorFiles = Object.keys(errors).join(', ');
        setUnityError(`Не удалось загрузить файлы игры: ${errorFiles}. Проверьте подключение к интернету и попробуйте перезагрузить страницу.`);
      } else {
        setFileLoadErrors({});
        console.log('✅ Все Unity файлы доступны');
      }
    };

    if (loaderUrl && dataUrl && frameworkUrl && codeUrl) {
      checkFilesAvailability();
    }
  }, [loaderUrl, dataUrl, frameworkUrl, codeUrl]);

  // Глобальный обработчик ошибок для WebGPU и сетевых ошибок
  useEffect(() => {
    const handleGlobalError = (event: ErrorEvent) => {
      console.error('🌐 Глобальная ошибка:', event.error);
      
      if (event.error && event.error.message) {
        const message = event.error.message.toLowerCase();
        
        if (message.includes('webgpu') || message.includes('wgpu') || message.includes('graphics api')) {
          console.warn('WebGPU error detected:', event.error);
          setUnityError('Ваше устройство не поддерживает современные графические технологии. Попробуйте обновить браузер или использовать другое устройство.');
        } else if (message.includes('network') || message.includes('fetch') || message.includes('failed to fetch')) {
          console.error('Network error detected:', event.error);
          setUnityError('Ошибка сети при загрузке игры. Проверьте подключение к интернету и попробуйте перезагрузить страницу.');
        } else if (message.includes('timeout') || message.includes('timed out')) {
          console.error('Timeout error detected:', event.error);
          setUnityError('Превышено время ожидания загрузки. Попробуйте перезагрузить страницу или очистить кэш браузера.');
        }
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('🌐 Необработанное отклонение промиса:', event.reason);
      
      if (event.reason && event.reason.message) {
        const message = event.reason.message.toLowerCase();
        
        if (message.includes('webgpu') || message.includes('wgpu')) {
          console.warn('WebGPU promise rejection:', event.reason);
          setUnityError('Ваше устройство не поддерживает современные графические технологии. Попробуйте обновить браузер или использовать другое устройство.');
        } else if (message.includes('network') || message.includes('fetch')) {
          console.error('Network promise rejection:', event.reason);
          setUnityError('Ошибка сети при загрузке игры. Проверьте подключение к интернету.');
        }
      }
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const loadingPercentage = Math.round(loadingProgression * 100);

  // Отслеживание прогресса загрузки для диагностики зависаний
  useEffect(() => {
    console.log('Unity loading progress:', loadingPercentage + '%', {
      progression: loadingProgression,
      isLoaded,
      loaderUrl,
      dataUrl,
      frameworkUrl,
      codeUrl
    });

    // Сохраняем последнее значение прогресса
    if (loadingPercentage !== lastProgressValue) {
      setLastProgressValue(loadingPercentage);
      console.log('Progress updated:', lastProgressValue + '% -> ' + loadingPercentage + '%');
    }
  }, [loadingProgression, loadingPercentage, isLoaded, lastProgressValue, loaderUrl, dataUrl, frameworkUrl, codeUrl]);

  // Обработка таймаута загрузки (если зависает на 90%)
  useEffect(() => {
    if (!isLoaded && loadingPercentage >= 90) {
      // Засекаем время начала зависания на 90%
      if (stuckAt90StartTime === null) {
        const startTime = Date.now();
        setStuckAt90StartTime(startTime);
        console.warn(`⚠️ Unity загрузка зависла на ${loadingPercentage}%`);
        console.warn('⏱️ Начинаем отсчет таймаута...');
        
        // Проверяем активные сетевые запросы
        if ('performance' in window && 'getEntriesByType' in performance) {
          const networkEntries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
          const unityFiles = networkEntries.filter(entry => 
            entry.name.includes('loader.js') || 
            entry.name.includes('data.br') || 
            entry.name.includes('framework.js.br') || 
            entry.name.includes('wasm.br')
          );
          
          console.log('📊 Активные сетевые запросы Unity файлов:');
          unityFiles.forEach(file => {
            const duration = file.duration || 0;
            const transferSize = file.transferSize || 0;
            const decodedBodySize = file.decodedBodySize || 0;
            console.log(`  - ${file.name.split('/').pop()}: ${duration.toFixed(0)}ms, ${(transferSize / 1024).toFixed(2)}KB`);
            
            if (duration > 30000) {
              console.warn(`  ⚠️ Файл ${file.name.split('/').pop()} загружается более 30 секунд!`);
            }
          });
        }
      }
      
      // Очищаем предыдущий таймаут
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }

      // Устанавливаем таймаут на 15 секунд после зависания на 90%
      const timeout = setTimeout(() => {
        if (!isLoaded && loadingPercentage >= 90 && stuckAt90StartTime) {
          const stuckDuration = Date.now() - stuckAt90StartTime;
          console.error(`❌ Unity загрузка таймаут на ${loadingPercentage}% после ${(stuckDuration / 1000).toFixed(1)} секунд`);
          
          // Проверяем ошибки загрузки файлов
          const errorDetails = Object.keys(fileLoadErrors).length > 0
            ? ` Проблемные файлы: ${Object.keys(fileLoadErrors).join(', ')}.`
            : '';
          
          setUnityError(
            `Загрузка игры зависла на ${loadingPercentage}% более 15 секунд.${errorDetails} ` +
            `Попробуйте перезагрузить страницу или очистить кэш браузера. ` +
            `Если проблема сохраняется, проверьте консоль браузера для деталей.`
          );
        }
      }, 15000); // 15 секунд

      setLoadingTimeout(timeout);
    } else if (isLoaded) {
      // Очищаем таймаут при успешной загрузке
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
        setLoadingTimeout(null);
      }
      if (stuckAt90StartTime !== null) {
        setStuckAt90StartTime(null);
        console.log('✅ Unity загрузка завершена успешно');
      }
    } else if (loadingPercentage < 90) {
      // Если прогресс упал ниже 90%, сбрасываем таймер
      if (stuckAt90StartTime !== null) {
        setStuckAt90StartTime(null);
        console.log(`📈 Прогресс загрузки: ${loadingPercentage}% (сброс таймера зависания)`);
      }
    }

    return () => {
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
    };
  }, [loadingPercentage, isLoaded, loadingTimeout, stuckAt90StartTime, fileLoadErrors]);

  // Проверка на зависание прогресса и автоматическая очистка кэша
  useEffect(() => {
    if (!isLoaded && loadingPercentage > 0 && loadingPercentage < 100 && !autoReloadTriggered) {
      const progressCheckTimeout = setTimeout(() => {
        if (!isLoaded && loadingPercentage === lastProgressValue && loadingPercentage < 100) {
          const stuckTime = stuckAt90StartTime ? Date.now() - stuckAt90StartTime : 0;
          console.warn(`⚠️ Unity загрузка не прогрессирует уже 10 секунд на ${loadingPercentage}%`);
          console.warn(`⏱️ Общее время зависания: ${(stuckTime / 1000).toFixed(1)} секунд`);
          
          // Проверяем, есть ли активные загрузки
          const hasActiveDownloads = checkActiveDownloads();
          
          if (!hasActiveDownloads) {
            console.warn('❌ Активных загрузок не обнаружено');
            console.warn('🔄 Автоматически очищаем кэш и перезагружаем страницу...');
            setUnityError('Загрузка игры зависла. Автоматически очищаем кэш и перезагружаем страницу...');
            autoClearCacheAndReload();
          } else {
            console.warn('📥 Обнаружены активные загрузки, продолжаем ожидание...');
            console.warn('📋 Проверьте следующие файлы:');
            console.warn(`  - Loader: ${loaderUrl}`);
            console.warn(`  - Data: ${dataUrl}`);
            console.warn(`  - Framework: ${frameworkUrl}`);
            console.warn(`  - Code: ${codeUrl}`);
          }
        }
      }, 10000); // 10 секунд

      return () => clearTimeout(progressCheckTimeout);
    }
  }, [loadingPercentage, lastProgressValue, isLoaded, stuckAt90StartTime, loaderUrl, dataUrl, frameworkUrl, codeUrl, autoReloadTriggered, checkActiveDownloads, autoClearCacheAndReload]);

  const { active, exit, enter } = handle;

  const fullScreenTitle = active
    ? 'Нажмите, чтобы выйти из полноэкранного режима'
    : 'Нажмите, чтобы войти в полноэкранный режим';

  return (
    <FullScreen className="fullScreenContainer" handle={handle}>
      <Box id="unity-container" className="unityContainer">
        {unityError ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'white',
            textAlign: 'center',
            padding: '20px'
          }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Ошибка загрузки игры
            </Typography>
            <Typography sx={{ marginBottom: 3, maxWidth: '400px' }}>
              {unityError}
            </Typography>
            {Object.keys(fileLoadErrors).length > 0 && (
              <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: 'rgba(255, 0, 0, 0.1)', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 'bold' }}>
                  Ошибки загрузки файлов:
                </Typography>
                {Object.entries(fileLoadErrors).map(([file, error]) => (
                  <Typography key={file} variant="body2" sx={{ fontSize: '12px', marginBottom: 0.5 }}>
                    • {file}: {error}
                  </Typography>
                ))}
              </Box>
            )}
            {autoReloadTriggered && (
              <Box sx={{ marginTop: 2, padding: 2, backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '14px' }}>
                  ⏳ Очистка кэша и перезагрузка...
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <>
            <Unity
              unityProvider={unityProvider}
              className="unityGame"
              devicePixelRatio={devicePixelRatio}
            />
            {!isLoaded && (
              <>
                <Box className="loadingContainer">
                  <LinearProgress
                    variant="determinate"
                    value={loadingPercentage}
                    className="linearProgress"
                  />
                  <Typography className="percentageContainer" sx={{
                    fontSize: 'clamp(8px, 2vw, 14px)'
                  }}>{`${loadingPercentage}%`}</Typography>
                </Box>
                {/* <Typography sx={{ 
                  color: 'rgb(225, 150, 160)',
                  textAlign: 'center', 
                  position: 'absolute',
                  bottom: '20%',
                  left: 0,
                  right: 0,
                  margin: '0 auto',
                  fontFamily: "'Comfortaa', sans-serif",
                  fontWeight: 700,
                  fontSize: '32px',
                }} className="pulseText">
                  {getLoadingText(loadingPercentage)}
                </Typography> */}
                {/* Lottie анимация кота снизу по центру */}
                <Box sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: '2%',
                  display: 'flex',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 10,
                }}>
                  <Lottie
                    animationData={catAnim}
                    loop={true}
                    style={{ width: 220, height: 90 }}
                  />
                </Box>
              </>
            )}
            {isFullScreenEnabled && (
              <Tooltip title={fullScreenTitle} placement="left" enterTouchDelay={0} leaveTouchDelay={3000}>
                <IconButton
                  className="fullScreenIconContainer"
                  onClick={active ? void exit : void enter}
                >
                  {active ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Box>
    </FullScreen>
  );
};
